import React, { useState, useEffect, ReactNode } from 'react';
import DSDialog from '../../../../design-system/DSDialog';
import DSDialogTitle from '../../../../design-system/DSDialogTitle';
import DSDialogContent from '../../../../design-system/DSDialogContent';
import DSDialogActions from '../../../../design-system/DSDialogActions';
import DSBox from '../../../../design-system/DSBox';
import DSGrid from '../../../../design-system/DSGrid';
import DSPrimaryButton from '../../../../design-system/DSPrimaryButton';
import DSSecondaryButton from '../../../../design-system/DSSecondaryButton';
import DSHeaderTypography from '../../../../design-system/Typography/DSHeaderTypography';
import DSValueTypography from '../../../../design-system/Typography/DSValueTypography';
import { DSTypography } from '../../../../design-system/Typography';
import { useTheme } from '@mui/material';

export type SelectItemModalProps<T> = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  items: T[];
  getItemId: (item: T) => string;
  selectedItemIds: string[];
  onConfirm: (selectedIds: string[]) => void;
  maxSelection?: number;
  renderItem: (item: T, isSelected: boolean, onToggle: () => void) => ReactNode;
  gridColumns?: number;
  confirmButtonText?: string;
  isLoading?: boolean;
  emptyMessage?: string;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  searchBar?: ReactNode;
};

function SelectItemModal<T>({
  isOpen,
  onClose,
  title,
  subtitle,
  items,
  getItemId,
  selectedItemIds,
  onConfirm,
  maxSelection,
  renderItem,
  gridColumns = 2,
  confirmButtonText = 'Ok',
  isLoading = false,
  emptyMessage = 'No items available.',
  fetchNextPage,
  isFetchingNextPage,
  hasNextPage,
  searchBar,
}: SelectItemModalProps<T>) {
  const { spacing } = useTheme();
  const [tempSelectedIds, setTempSelectedIds] =
    useState<string[]>(selectedItemIds);

  useEffect(() => {
    if (isOpen) {
      setTempSelectedIds(selectedItemIds);
    }
  }, [isOpen]);

  const handleToggleItem = (itemId: string) => {
    if (tempSelectedIds.includes(itemId)) {
      setTempSelectedIds(tempSelectedIds.filter((id) => id !== itemId));
    } else if (maxSelection === 1) {
      setTempSelectedIds([itemId]);
    } else if (
      maxSelection === undefined ||
      tempSelectedIds.length < maxSelection
    ) {
      setTempSelectedIds([...tempSelectedIds, itemId]);
    }
  };

  const handleConfirm = () => {
    onConfirm(tempSelectedIds);
    onClose();
  };

  const handleCancel = () => {
    setTempSelectedIds(selectedItemIds);
    onClose();
  };

  return (
    <DSDialog
      open={isOpen}
      maxWidth="xl"
      onClose={handleCancel}
      PaperProps={{ sx: { minWidth: { xs: '90vw', md: '700px' } } }}
    >
      <DSDialogTitle>
        <DSBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <DSHeaderTypography>{title}</DSHeaderTypography>
          {subtitle && <DSValueTypography>{subtitle}</DSValueTypography>}
        </DSBox>
      </DSDialogTitle>

      {searchBar && <DSBox sx={{ px: 3, pb: 1 }}>{searchBar}</DSBox>}

      <DSDialogContent
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        sx={{
          padding: 0,
          height: '60vh',
          overflowY: 'auto',
        }}
      >
        {isLoading ? (
          <DSBox
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DSTypography variant="body2">Loading...</DSTypography>
          </DSBox>
        ) : items && items.length > 0 ? (
          <DSGrid
            container
            spacing={2}
            columns={{ xs: 1, sm: 1 }}
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr',
                md: `repeat(${gridColumns}, 1fr)`,
                lg: `repeat(${gridColumns}, 1fr)`,
                xl: `repeat(${gridColumns}, 1fr)`,
              },
              padding: { sm: spacing(2), md: spacing(4) },
            }}
          >
            {items.map((item) => {
              const itemId = getItemId(item);
              const isSelected = tempSelectedIds.includes(itemId);
              const onToggle = () => handleToggleItem(itemId);
              return (
                <DSGrid key={itemId}>
                  {renderItem(item, isSelected, onToggle)}
                </DSGrid>
              );
            })}
          </DSGrid>
        ) : (
          <DSBox
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DSTypography variant="body2">{emptyMessage}</DSTypography>
          </DSBox>
        )}
      </DSDialogContent>

      <DSDialogActions sx={{ justifyContent: 'flex-end', padding: 3, gap: 2 }}>
        <DSSecondaryButton onClick={handleCancel}>Cancel</DSSecondaryButton>
        <DSPrimaryButton onClick={handleConfirm}>
          {confirmButtonText}
        </DSPrimaryButton>
      </DSDialogActions>
    </DSDialog>
  );
}

export default SelectItemModal;
